import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Lock, Mail, MapPin, Phone, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuthLayout } from '@/components/layout/auth-layout';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, Input } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/utils';

interface PublicRepairStatus {
  number: string;
  device: string;
  status: string;
  ready: boolean;
  pickedUp: boolean;
  outcome: string | null;
  receivedAt: string;
  estimatedCompletionAt: string | null;
  readyAt: string | null;
  pickedUpAt: string | null;
  steps: { text: string; createdAt: string }[];
  quote: { status: string; gross: string; validUntil: string | null } | null;
  tenant: { name: string; phone: string | null; email: string | null; address: string | null };
}

/** Statusseite ohne Anmeldung (QR-Code auf dem Annahmeschein) – Anzeige erst nach PIN-Eingabe. */
export function PublicRepairStatusPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [pin, setPin] = useState('');
  const check = useQuery({ queryKey: ['public-repair', token], queryFn: () => api<{ tenant: string; locked: boolean }>(`/public/repair-status/${encodeURIComponent(token)}`), retry: false });
  const unlock = useMutation({ mutationFn: () => api<PublicRepairStatus>(`/public/repair-status/${encodeURIComponent(token)}`, { method: 'POST', body: { pin } }) });
  const s = unlock.data;

  if (check.isError) {
    return (
      <AuthLayout title="Statusabfrage" description="Dieser Link ist ungültig oder nicht mehr aktiv.">
        <p className="text-sm text-muted-foreground">Bitte prüfen Sie den Annahmeschein oder melden Sie sich im Kundenportal an.</p>
        <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">
          Zum Kundenportal
        </Link>
      </AuthLayout>
    );
  }

  if (!s) {
    return (
      <AuthLayout title="Reparaturstatus" description="Bitte geben Sie die PIN von Ihrem Annahmeschein ein.">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            unlock.mutate();
          }}
        >
          {(check.data?.locked || (unlock.error instanceof ApiError && unlock.error.status === 429)) && (
            <Alert variant="destructive">Zu viele Fehlversuche. Bitte versuchen Sie es in 15 Minuten erneut.</Alert>
          )}
          {unlock.error && !(unlock.error instanceof ApiError && unlock.error.status === 429) && <Alert variant="destructive">{errorMessage(unlock.error)}</Alert>}
          <Field label="PIN (6 Ziffern)" htmlFor="pin">
            <Input
              id="pin"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center font-mono text-2xl tracking-[0.5em]"
              autoFocus
            />
          </Field>
          <Button type="submit" loading={unlock.isPending} disabled={pin.length !== 6}>
            <Lock /> Status anzeigen
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <div className="min-h-dvh bg-gradient-to-b from-primary/8 via-background to-background px-4 py-8">
      <div className="mx-auto grid max-w-xl gap-4">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">{s.tenant.name}</p>
          <h1 className="text-xl font-semibold">Reparatur {s.number}</h1>
          <p className="text-sm text-muted-foreground">{s.device}</p>
        </div>
        <Card>
          <CardContent className="grid gap-3 pt-5 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Stand</span>
              <Badge variant={s.ready ? 'success' : 'default'} className="text-sm">
                {s.status}
              </Badge>
            </div>
            {s.ready && (
              <Alert variant="success">
                <CheckCircle2 className="mr-1 inline size-4" /> Ihr Gerät ist abholbereit{s.outcome ? ` (${s.outcome})` : ''}. Bitte bringen Sie den Annahmeschein mit.
              </Alert>
            )}
            {s.pickedUp && s.pickedUpAt && <p>Abgeholt am {formatDateTime(s.pickedUpAt)}.</p>}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">Angenommen</dt>
              <dd>{formatDateTime(s.receivedAt)}</dd>
              {s.estimatedCompletionAt && !s.ready && !s.pickedUp && (
                <>
                  <dt className="text-muted-foreground">Voraussichtlich fertig</dt>
                  <dd>{formatDate(s.estimatedCompletionAt)}</dd>
                </>
              )}
              {s.readyAt && (
                <>
                  <dt className="text-muted-foreground">Fertiggestellt</dt>
                  <dd>{formatDateTime(s.readyAt)}</dd>
                </>
              )}
            </dl>
            {s.quote && (
              <div className="rounded-md border p-3">
                <p className="font-medium">Kostenvoranschlag: {s.quote.gross}</p>
                <p className="text-muted-foreground">
                  {s.quote.status}
                  {s.quote.validUntil ? ` · gültig bis ${formatDate(s.quote.validUntil)}` : ''}
                </p>
                {s.quote.status === 'Entscheidung ausstehend' && (
                  <p className="mt-1 text-muted-foreground">Freigeben können Sie ihn im Kundenportal oder telefonisch.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        {s.steps.length > 0 && (
          <Card>
            <CardContent className="grid gap-3 pt-5 text-sm">
              <p className="font-medium">Durchgeführte Arbeiten</p>
              <ul className="grid gap-2">
                {s.steps.map((st, i) => (
                  <li key={i} className="flex gap-2">
                    <Wrench className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <span>
                      {st.text}
                      <span className="block text-xs text-muted-foreground">{formatDateTime(st.createdAt)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="grid gap-1.5 pt-5 text-sm">
            <p className="font-medium">Fragen? {s.tenant.name}</p>
            {s.tenant.phone && (
              <a href={`tel:${s.tenant.phone}`} className="inline-flex items-center gap-2 text-primary">
                <Phone className="size-4" /> {s.tenant.phone}
              </a>
            )}
            {s.tenant.email && (
              <a href={`mailto:${s.tenant.email}`} className="inline-flex items-center gap-2 text-primary">
                <Mail className="size-4" /> {s.tenant.email}
              </a>
            )}
            {s.tenant.address && (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <MapPin className="size-4" /> {s.tenant.address}
              </span>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground">
          Mehr Details und Belege im{' '}
          <Link to="/login" className="text-primary hover:underline">
            Kundenportal
          </Link>
        </p>
      </div>
    </div>
  );
}
